import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Button, Input, Select } from '../components/ui-kit';
import { showToast, ToastContainer } from '../utils/toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const Signup = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    role: '',
    name: '',
    business_name: '',
    country: '',
    region: '',
    city: ''
  });
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    fetchCountries();
  }, []);

  useEffect(() => {
    if (formData.country) {
      fetchRegions(formData.country);
    }
  }, [formData.country]);

  useEffect(() => {
    if (formData.country && formData.region) {
      fetchCities(formData.country, formData.region);
    }
  }, [formData.country, formData.region]);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/locations/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchRegions = async (countryCode) => {
    try {
      const response = await axios.get(`${API}/locations/regions/${countryCode}`);
      setRegions(response.data);
    } catch (error) {
      console.error('Failed to fetch regions:', error);
    }
  };

  const fetchCities = async (countryCode, region) => {
    try {
      const response = await axios.get(`${API}/locations/cities/${countryCode}/${region}`);
      setCities(response.data);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
    
    if (field === 'country') {
      setFormData(prev => ({ ...prev, region: '', city: '' }));
      setRegions([]);
      setCities([]);
    } else if (field === 'region') {
      setFormData(prev => ({ ...prev, city: '' }));
      setCities([]);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.password) newErrors.password = 'Password is required';
    if (!formData.confirmPassword) newErrors.confirmPassword = 'Please confirm password';
    if (!formData.role) newErrors.role = 'Please select account type';
    
    // Business name is required for business users
    if (formData.role === 'BUSINESS' && !formData.business_name) {
      newErrors.business_name = 'Business name is required';
    }
    
    if (formData.password && formData.password.length < 10) {
      newErrors.password = 'Password must be at least 10 characters';
    }
    if (formData.password && !/[A-Za-z]/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one letter';
    }
    if (formData.password && !/\d/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showToast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...signupData } = formData;
      const response = await axios.post(`${API}/auth/signup`, signupData);

      login(response.data.access_token, response.data.user);
      showToast.success('Account created successfully!');

      if (response.data.user.role === 'BUSINESS') {
        navigate('/business/dashboard');
      } else {
        navigate('/salesperson/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = 'Signup failed';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(e => e.msg || e.message || String(e)).join('. ');
      }
      showToast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <ToastContainer />
      <div className="w-full max-w-2xl">
        <div className="rounded-2xl bg-brand-surface p-8 shadow-ring ring-1 ring-zinc-800">
          <div className="text-center mb-8">
            <img src="/logo.svg" alt="Sale Sphere" className="h-16 w-16 mx-auto mb-4" />
            <h1 className="font-display text-3xl text-brand-text mb-2">Create Account</h1>
            <p className="text-brand-muted">Join Sale Sphere today</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                id="name"
                label="Full Name"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                error={errors.name}
                required
                data-testid="signup-name-input"
              />

              <Input
                id="email"
                type="email"
                label="Email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                error={errors.email}
                required
                data-testid="signup-email-input"
              />

              <Input
                id="password"
                type="password"
                label="Password"
                placeholder="Min 10 chars, letter + number"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                error={errors.password}
                required
                data-testid="signup-password-input"
              />

              <Input
                id="confirmPassword"
                type="password"
                label="Confirm Password"
                placeholder="Confirm password"
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                error={errors.confirmPassword}
                required
                data-testid="signup-confirm-password-input"
              />

              <div className="md:col-span-2">
                <Select
                  id="role"
                  label="Account Type"
                  value={formData.role}
                  onChange={(e) => handleChange('role', e.target.value)}
                  error={errors.role}
                  required
                  data-testid="signup-role-select"
                >
                  <option value="">Select account type</option>
                  <option value="BUSINESS">Business</option>
                  <option value="SALESPERSON">Salesperson</option>
                </Select>
              </div>

              {formData.role === 'BUSINESS' && (
                <div className="md:col-span-2">
                  <Input
                    id="business_name"
                    label="Business Name"
                    placeholder="Your Company Inc."
                    value={formData.business_name}
                    onChange={(e) => handleChange('business_name', e.target.value)}
                    error={errors.business_name}
                    required
                    data-testid="signup-business-name-input"
                  />
                </div>
              )}

              {formData.role === 'SALESPERSON' && (
                <>
                  <Select
                    id="country"
                    label="Country (Optional)"
                    value={formData.country}
                    onChange={(e) => handleChange('country', e.target.value)}
                    data-testid="signup-country-select"
                  >
                    <option value="">Select country</option>
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>
                        {country.name}
                      </option>
                    ))}
                  </Select>

                  {formData.country && (
                    <Select
                      id="region"
                      label="Region/State"
                      value={formData.region}
                      onChange={(e) => handleChange('region', e.target.value)}
                      data-testid="signup-region-select"
                    >
                      <option value="">Select region</option>
                      {regions.map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </Select>
                  )}

                  {formData.region && (
                    <Select
                      id="city"
                      label="City"
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      data-testid="signup-city-select"
                    >
                      <option value="">Select city</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </Select>
                  )}
                </>
              )}
            </div>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              data-testid="signup-submit-button"
            >
              Create Account
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-brand-muted">
              Already have an account?{' '}
              <Link to="/login" className="text-brand-gold hover:text-brand-gold600 font-medium transition-colors" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
